// packages/api-client/src/services/billingService.ts

import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  ServiceResponse,
  PaginatedResponse,
  UserCreditsResponse,
  CreditTransactionItem,
  LLMUsageSummaryResponse,
  CreateTopupInvoicePayload,
  CreateTopupInvoiceResponse,
  // --- Import the corrected investment types ---
  InvestInAgentPayload,
  InvestCreateResponse,
} from '@dopomogai/types';

export const billingService = {
  // =================================================================
  // ==                    PAYMENT & INVESTMENT                   ==
  // =================================================================

  /**
   * Creates a Monobank Invoice for a custom number of credits.
   * @param payload - An object containing the number of credits to purchase.
   * @returns A promise that resolves with the Monobank invoice details or an API error.
   */
  createTopupInvoice: (payload: CreateTopupInvoicePayload): ServiceResponse<CreateTopupInvoiceResponse> => {
    return getApiClient().post<CreateTopupInvoiceResponse>('/billing/create-topup-invoice', payload)
      .then(response => response.data)
      .catch(error => handleApiError(error, 'creating Monobank top-up invoice'));
  },




  // =================================================================
  // ==                   ACCOUNT INFO METHODS                    ==
  // =================================================================

  /**
   * Retrieves the user's current credit balance.
   */
  getAccountCredits: (): ServiceResponse<UserCreditsResponse> => {
    return getApiClient().get<UserCreditsResponse>('/users/me/credits')
      .then(response => response.data)
      .catch(error => handleApiError(error, 'fetching account credits'));
  },
  
  /**
   * Retrieves a paginated list of the user's credit transactions.
   */
  getCreditTransactions: (limit = 20, offset = 0): ServiceResponse<PaginatedResponse<CreditTransactionItem>> => {
    const params = { limit, offset };
    return getApiClient().get<PaginatedResponse<CreditTransactionItem>>('/users/me/credit-transactions', { params })
      .then(response => response.data)
      .catch(error => handleApiError(error, 'fetching credit transactions'));
  },

  /**
   * Retrieves an aggregated summary of LLM usage over a specified period.
   */
  getUsageSummary: (period: 'today' | 'last_7_days' | 'current_month' | 'last_30_days' = "current_month"): ServiceResponse<LLMUsageSummaryResponse> => {
    const params = { period };
    return getApiClient().get<LLMUsageSummaryResponse>('/users/me/usage-summary', { params })
      .then(response => response.data)
      .catch(error => handleApiError(error, 'fetching usage summary'));
  },
};